import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionStore } from 'src/store/sessionStore';
import { getUserIdFromToken } from 'src/utils/authUtils';
import { useGetOnboardingState } from 'src/api/mutations/useGetOnboardingState';
import { computeOnboardingProgress } from 'src/utils/onboardingUtils';
import './CompleteOnboardingBanner.scss';

export const CompleteOnboardingBanner: FC = () => {
  const navigate = useNavigate();
  const token = sessionStore((s) => s.token);
  const currentUser = sessionStore((s) => s.currentUser);
  const userId = getUserIdFromToken(token) || currentUser?.uid;

  const { data: onboardingData, isLoading } = useGetOnboardingState(userId || undefined);

  if (!userId) {
    return null;
  }

  // Prevent flash of banner while fetching state if no cached onboarding state exists yet
  if (isLoading && !onboardingData && !currentUser?.onboarding) {
    return null;
  }

  const onboarding = onboardingData?.onboarding ?? currentUser?.onboarding;
  const progress = computeOnboardingProgress(onboarding);

  // The banner should only be visible when the trainer has skipped or not finished onboarding
  if (progress.isFullyCompleted) {
    return null;
  }

  const handleResume = () => {
    navigate('/onboarding-trainer?resume=true');
  };

  const getStepProgressText = () => {
    if (progress.completedStepsCount <= 0) {
      return 'Setup Incomplete (0/4)';
    }
    return `${progress.completedStepsCount}/4 steps completed`;
  };

  return (
    <div className="complete-onboarding-banner" role="alert">
      <div className="banner-left">
        <div className="banner-icon-container" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
        <div className="banner-text-content">
          <div className="banner-title-row">
            <h2 className="banner-title">Complete your onboarding</h2>
            <span className="banner-step-pill">{getStepProgressText()}</span>
          </div>
          <p className="banner-description">
            Complete your onboarding to set up your experience, coaching specialties, and weekly
            schedule so clients can find and book sessions with you.
          </p>
        </div>
      </div>
      <div className="banner-right">
        <button
          type="button"
          className="banner-action-btn"
          onClick={handleResume}
          aria-label="Finish onboarding now"
        >
          <span>Complete Onboarding</span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14" />
            <path d="M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};
